/**
 * buffItems.ts — 버프 아이템 시스템 (Sprint 3)
 *
 * 아이템 종류:
 *   luck_charm   — 행운부적: 다음 N회 성공률 +%(부적 강도에 따라)
 *   guarantee    — 보장강화권: 다음 강화 100% 성공 1회
 *   destroy_guard — 파괴방지권: 다음 실패 시 파괴 안됨 1회 (기존 방지권과 별개 consumable)
 *
 * 순수 로직. 브라우저/PIXI 금지.
 */

// ────────────────────────────────────────────────
//  상수
// ────────────────────────────────────────────────

/** 행운부적 성공률 보너스 (소수) */
export const LUCK_CHARM_BONUS = 0.05; // +5%

// ────────────────────────────────────────────────
//  상태 타입
// ────────────────────────────────────────────────

export interface BuffItemsState {
    /** 행운부적 보유 수 */
    luck_charm: number;
    /** 보장강화권 보유 수 */
    guarantee: number;
    /** 파괴방지권 보유 수 */
    destroy_guard: number;
    /** 행운부적 활성 중 여부 (사용 = 다음 1회 보너스) */
    luckCharmActive: boolean;
    /** 보장강화권 활성 중 여부 */
    guaranteeActive: boolean;
    /** 파괴방지권 활성 중 여부 */
    destroyGuardActive: boolean;
}

export function emptyBuffItems(): BuffItemsState {
    return {
        luck_charm: 0,
        guarantee: 0,
        destroy_guard: 0,
        luckCharmActive: false,
        guaranteeActive: false,
        destroyGuardActive: false,
    };
}

// ────────────────────────────────────────────────
//  아이템 사용
// ────────────────────────────────────────────────

/** 행운부적 사용 (보유 수 -1, 활성화). 보유 없으면 null */
export function useLuckCharm(state: BuffItemsState): BuffItemsState | null {
    if (state.luck_charm <= 0) return null;
    return { ...state, luck_charm: state.luck_charm - 1, luckCharmActive: true };
}

/** 보장강화권 사용. 보유 없으면 null */
export function useGuarantee(state: BuffItemsState): BuffItemsState | null {
    if (state.guarantee <= 0) return null;
    return { ...state, guarantee: state.guarantee - 1, guaranteeActive: true };
}

/** 파괴방지권 사용. 보유 없으면 null */
export function useDestroyGuard(state: BuffItemsState): BuffItemsState | null {
    if (state.destroy_guard <= 0) return null;
    return { ...state, destroy_guard: state.destroy_guard - 1, destroyGuardActive: true };
}

// ────────────────────────────────────────────────
//  강화 사이클 처리
// ────────────────────────────────────────────────

/**
 * 강화 후 활성 아이템 소모 처리.
 * @param state 현재 버프 상태
 * @param success 강화 성공 여부
 * @param destroyed 파괴 여부 (success=false 일 때만 의미)
 * @returns 새 상태 + 보너스 정보
 */
export function consumeBuffOnEnhance(
    state: BuffItemsState,
    success: boolean,
    destroyed: boolean,
): {
    newState: BuffItemsState;
    hadLuckCharm: boolean;
    hadGuarantee: boolean;
    hadDestroyGuard: boolean;
} {
    const hadLuckCharm = state.luckCharmActive;
    const hadGuarantee = state.guaranteeActive;
    const hadDestroyGuard = state.destroyGuardActive;

    let newState = { ...state };

    // 행운부적: 성공/실패 상관없이 사용 후 소모
    if (hadLuckCharm) {
        newState = { ...newState, luckCharmActive: false };
    }
    // 보장강화권: 강화 후 소모
    if (hadGuarantee) {
        newState = { ...newState, guaranteeActive: false };
    }
    // 파괴방지권: 파괴 발생 시에만 소모 (방지 역할 수행)
    if (hadDestroyGuard && !success && destroyed) {
        newState = { ...newState, destroyGuardActive: false };
    } else if (!success && !destroyed) {
        // 파괴 없는 실패 (보호됨) — 파괴방지권 미소모
    }

    return { newState, hadLuckCharm, hadGuarantee, hadDestroyGuard };
}

/**
 * 행운부적이 활성화된 경우 기본 성공률에 보너스 추가.
 * 절대 100%는 안 됨 — cap 0.99.
 */
export function applyLuckCharmBonus(baseRate: number, active: boolean): number {
    if (!active) return baseRate;
    return Math.min(0.99, baseRate + LUCK_CHARM_BONUS);
}

/**
 * 보장강화권 활성 시 성공 강제.
 */
export function applyGuarantee(active: boolean): boolean {
    return active;
}

// ────────────────────────────────────────────────
//  아이템 추가 (용광로 제작 결과로 호출)
// ────────────────────────────────────────────────

export type BuffItemKey = 'luck_charm' | 'guarantee' | 'destroy_guard';

export function addBuffItem(
    state: BuffItemsState,
    key: BuffItemKey,
    count: number = 1,
): BuffItemsState {
    return { ...state, [key]: state[key] + count };
}
